/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 権限管理ページのユーザー管理タブ。ユーザー一覧・ロール割当・検索・新規追加を提供する。
 * @graph-connects /permissions/user-roles [calls] ユーザーロール管理
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { useAuth } from '../../hooks/useAuth.js';
import { useRoles } from '../../hooks/useRoles.js';
import { useUserRoles, useSetUserRoles } from '../../hooks/useUserRoles.js';
import { ALL_PAGE_KEYS } from '../../nav.js';

/** @graph-connects none */
const TD = 'border-b border-[var(--color-border)] p-2';
/** @graph-connects none */
const INPUT =
  'w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2 py-1 text-xs';
/** @graph-connects none */
const DEFAULT_SORT_ORDER = 999;

/** @graph-connects none */
export function UsersTab() {
  const { user: userData } = useAuth();
  const { data: rolesData, isLoading: rolesLoading } = useRoles();
  const { data: userRolesData, isLoading: urLoading } = useUserRoles();
  const setUserRolesMut = useSetUserRoles();
  const [search, setSearch] = useState('');
  const [addEmail, setAddEmail] = useState('');

  const roles = rolesData?.roles ?? [];
  const sortedRoles = [...roles].sort(
    (a, b) => (a.sortOrder ?? DEFAULT_SORT_ORDER) - (b.sortOrder ?? DEFAULT_SORT_ORDER),
  );
  const userRoles = userRolesData?.userRoles ?? [];
  const currentEmail = userData?.email ?? '';

  const filtered = search
    ? userRoles.filter((ur) => {
        const q = search.toLowerCase();
        return (
          ur.email.toLowerCase().includes(q) || (ur.nickname?.toLowerCase().includes(q) ?? false)
        );
      })
    : userRoles;

  const getUserPermissionCount = (roleIds: string[]): number => {
    const perms = new Set<string>(['home']);
    for (const role of roles) {
      if (roleIds.includes(role.id)) {
        for (const p of role.permissions) perms.add(p);
      }
    }
    return perms.size;
  };

  const totalKeys = ALL_PAGE_KEYS.length;

  const handleToggleRole = (email: string, currentRoleIds: string[], roleId: string) => {
    const next = currentRoleIds.includes(roleId)
      ? currentRoleIds.filter((id) => id !== roleId)
      : [...currentRoleIds, roleId];
    setUserRolesMut.mutate({ email, roleIds: next });
  };

  const handleAddUser = () => {
    if (!addEmail.trim()) return;
    setUserRolesMut.mutate(
      { email: addEmail.trim(), roleIds: [] },
      {
        onSuccess: () => {
          setAddEmail('');
        },
      },
    );
  };

  if (rolesLoading || urLoading) {
    return (
      <Card className="p-10 text-center text-sm text-[var(--color-text-muted)]">読み込み中...</Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center gap-3 border-b border-[var(--color-border)] px-4 py-3">
        <input
          className={`${INPUT} max-w-[300px]`}
          placeholder="メール・ニックネームで検索..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
        />
        <div className="ml-auto flex items-center gap-2">
          <input
            className={`${INPUT} w-[240px]`}
            placeholder="新規ユーザーのメールアドレス"
            value={addEmail}
            onChange={(e) => {
              setAddEmail(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAddUser();
            }}
          />
          <Button className="text-xs" onClick={handleAddUser} disabled={!addEmail.trim()}>
            <Plus className="h-3.5 w-3.5" />
            追加
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className={`${TD} text-left font-semibold`}>ユーザー</th>
              <th className={`${TD} text-left font-semibold`} style={{ width: 80 }}>
                雇用形態
              </th>
              <th className={`${TD} text-left font-semibold`} style={{ width: 120 }}>
                役職
              </th>
              <th className={`${TD} text-left font-semibold`}>ロール</th>
              <th className={`${TD} text-right font-semibold`} style={{ width: 120 }}>
                有効ページ
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((ur) => {
              const permCount = getUserPermissionCount(ur.roleIds);
              const pct = Math.round((permCount / totalKeys) * 100);
              const initial = (ur.nickname?.[0] ?? ur.email[0]).toUpperCase();
              return (
                <tr key={ur.email} className="hover:bg-[var(--color-bg-hover)]">
                  <td className={TD}>
                    <div className="flex items-center gap-2">
                      <div
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                        style={{ background: 'var(--color-accent-primary)' }}
                      >
                        {initial}
                      </div>
                      <div className="min-w-0">
                        {ur.nickname && <div className="font-semibold">{ur.nickname}</div>}
                        <div
                          className={ur.nickname ? 'text-[var(--color-text-muted)]' : 'font-medium'}
                        >
                          {ur.email}
                        </div>
                        {ur.email === currentEmail && (
                          <span className="text-[10px] text-[var(--color-text-muted)]">あなた</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className={`${TD} text-[var(--color-text-secondary)]`}>
                    {ur.employmentType ?? ''}
                  </td>
                  <td className={`${TD} text-[var(--color-text-secondary)]`}>
                    {ur.position ?? ''}
                  </td>
                  <td className={TD}>
                    <div className="flex flex-wrap gap-1">
                      {sortedRoles.map((role) => {
                        const assigned = ur.roleIds.includes(role.id);
                        return (
                          <button
                            key={role.id}
                            type="button"
                            className={`rounded px-2 py-0.5 text-[10px] font-semibold transition-colors ${
                              assigned
                                ? 'bg-[var(--color-accent-primary-alpha)] text-[var(--color-accent-primary)]'
                                : 'border border-dashed border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-accent-primary)] hover:text-[var(--color-accent-primary)]'
                            }`}
                            onClick={() => {
                              handleToggleRole(ur.email, ur.roleIds, role.id);
                            }}
                            title={assigned ? `${role.name} を解除` : `${role.name} を割当`}
                          >
                            {assigned ? role.name : `+ ${role.name}`}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td className={`${TD} text-right`}>
                    <div className="flex items-center justify-end gap-2">
                      <div className="h-1 w-14 overflow-hidden rounded bg-[var(--color-bg-elevated)]">
                        <div
                          className="h-full rounded bg-[var(--color-accent-primary)]"
                          style={{ width: `${String(pct)}%` }}
                        />
                      </div>
                      <span className="tabular-nums text-[var(--color-text-secondary)]">
                        {permCount}/{totalKeys}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className={`${TD} py-8 text-center text-[var(--color-text-muted)]`}>
                  {search ? '該当するユーザーが見つかりません' : 'ユーザーが登録されていません'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
